import { useState, type FormEvent } from 'react';
import { api, type Job } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useJobs } from '../components/JobToast';

interface DiscoverResult {
  handle: string;
  type: string;
  id: string;
  title: string;
  link: string;
  saved?: boolean;
}

export function DiscoverPage() {
  const { trackJob } = useJobs();
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(25);
  const [results, setResults] = useState<DiscoverResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const onSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs/discover', {
        query: query.trim(),
        limit,
      });
      const done = await trackJob(job.id, `Ontdekken: ${query.trim()}`);
      if (done.status === 'failed') {
        setError(done.error || 'Discover mislukt');
        setResults([]);
      } else {
        const result = done.result as { results?: DiscoverResult[] } | null;
        setResults(result?.results ?? []);
        setInfo(`${result?.results?.length ?? 0} resultaten`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discover mislukt');
    } finally {
      setBusy(false);
    }
  };

  const save = async (handle: string) => {
    await api.post('/api/sources', { handle, platform: 'telegram' });
    setResults((prev) => prev.map((r) => (r.handle === handle ? { ...r, saved: true } : r)));
  };

  const [previewHandle, setPreviewHandle] = useState<string | null>(null);
  const [previewMsgs, setPreviewMsgs] = useState<
    Array<{ id: string; date_utc: string; text: string | null; has_media: boolean; media_label: string | null }>
  >([]);

  const preview = async (handle: string) => {
    setBusy(true);
    setError(null);
    setPreviewHandle(handle);
    setPreviewMsgs([]);
    try {
      const { job } = await api.post<{ job: Job }>('/api/jobs/preview', { handle, limit: 8 });
      const done = await trackJob(job.id, `Preview ${handle}`);
      if (done.status === 'failed') {
        setError(done.error || 'Preview mislukt');
        return;
      }
      const result = done.result as { messages?: typeof previewMsgs } | null;
      setPreviewMsgs(result?.messages ?? []);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>Kanalen ontdekken</h1>
      <p className="page-lead">
        Zoek openbare Telegram-chats en gebruikers. De zoekopdracht loopt asynchroon — je kunt doorwerken.
      </p>

      <form className="panel toolbar" onSubmit={(e) => void onSearch(e)}>
        <div className="field" style={{ flex: 1 }}>
          <label>Trefwoord</label>
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="bijv. rotterdam"
            autoFocus
          />
        </div>
        <div className="field">
          <label>Limiet</label>
          <input
            className="input input-sm"
            type="number"
            min={1}
            max={100}
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          />
        </div>
        <button className="btn" disabled={busy} type="submit">
          {busy ? 'Bezig…' : 'Zoeken'}
        </button>
      </form>

      {error && <div className="alert error">{error}</div>}
      {info && <div className="muted" style={{ marginTop: 12 }}>{info}</div>}

      <div className="msg-list">
        {results.map((r) => (
          <article className="msg" key={r.handle}>
            <div className="msg-head">
              <div>
                <strong>{r.handle}</strong>{' '}
                <span className="muted">
                  {r.type} · {r.id}
                </span>
              </div>
              <div className="row">
                <button className="btn btn-ghost btn-sm" type="button" disabled={busy} onClick={() => void preview(r.handle)}>
                  Preview
                </button>
                {r.saved ? (
                  <span className="badge ok">Opgeslagen</span>
                ) : (
                  <button className="btn btn-sm" type="button" onClick={() => void save(r.handle)}>
                    Opslaan als bron
                  </button>
                )}
              </div>
            </div>
            <div className="muted">{r.title}</div>
            <div>
              <a href={r.link} target="_blank" rel="noopener noreferrer">
                {r.link}
              </a>
            </div>
            {previewHandle === r.handle && (
              <div className="msg-list" style={{ marginTop: 10 }}>
                {previewMsgs.map((m) => (
                  <article className="msg" key={m.id}>
                    <div className="msg-head">
                      <span>{formatDateTime(m.date_utc)}</span>
                      {m.has_media && <span className="badge">{m.media_label || 'Media'}</span>}
                    </div>
                    <pre className="msg-text">{m.text || '[leeg]'}</pre>
                  </article>
                ))}
                {previewMsgs.length === 0 && !busy && <div className="muted">Geen berichten in preview.</div>}
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
}
